/**
 * Wishlist heart — one-tap toggle to save a neighbourhood to the
 * signed-in buyer's shortlist. Keeps a module-level cache of the
 * hearted-set so multiple hearts on the same page stay in sync.
 *
 * Storage:
 *   • Signed-in — POST/DELETE /api/wishlist keyed by lat/lng grid
 *   • Anonymous — localStorage `lovinahome_wishlist` (array of items)
 *
 * On sign-in, callers can call `syncLocalToServer()` to merge any
 * anonymous hearts into the account.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Heart } from 'lucide-react';

const LS_KEY = 'lovinahome_wishlist';
const _grid = (lat, lng) => `${Number(lat).toFixed(3)}|${Number(lng).toFixed(3)}`;

// Module-level "hearted" set + subscribers so multiple hearts on a
// page reflect toggles immediately without another network round-trip.
const _hearted = new Set();
const _subs = new Set();
const _notify = () => _subs.forEach((fn) => fn(new Set(_hearted)));

const _readLocal = () => {
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};
const _writeLocal = (arr) => {
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(arr)); } catch { /* private */ }
};

// One-shot loader on first mount — pulls the server list when authed
// and hydrates the module `_hearted` set + reflects local state.
let _loaded = false;
const _loadOnce = async () => {
  if (_loaded) return;
  _loaded = true;
  const token = window.localStorage.getItem('token');
  if (token) {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wishlist`,
                              { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const rows = await r.json();
        rows.forEach((it) => _hearted.add(_grid(it.lat, it.lng)));
      }
    } catch { /* silent */ }
  }
  _readLocal().forEach((it) => _hearted.add(_grid(it.lat, it.lng)));
  _notify();
};

export const WishlistHeart = ({ lat, lng, label, size = 'sm', className = '' }) => {
  const [heartedSet, setHeartedSet] = useState(_hearted);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    _subs.add(setHeartedSet);
    _loadOnce();
    return () => { _subs.delete(setHeartedSet); };
  }, []);

  const key = _grid(lat, lng);
  const on = heartedSet.has(key);

  const toggle = useCallback(async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (lat == null || lng == null) return;
    setBusy(true);
    const wasOn = _hearted.has(key);

    // Optimistic UI: flip immediately, revert on error.
    if (wasOn) _hearted.delete(key); else _hearted.add(key);
    _notify();

    const token = window.localStorage.getItem('token');
    let ok = true;
    try {
      if (token) {
        if (wasOn) {
          const r = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/wishlist?lat=${lat}&lng=${lng}`,
            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
          );
          ok = r.ok;
        } else {
          const r = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/wishlist`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json',
                           Authorization: `Bearer ${token}` },
              body: JSON.stringify({ lat, lng, label: label || 'Area' }),
            },
          );
          ok = r.ok;
        }
      }
      // Mirror to localStorage regardless — persists across sessions
      // even if the network write failed / user is anonymous.
      const cur = _readLocal();
      if (wasOn) {
        _writeLocal(cur.filter((it) => _grid(it.lat, it.lng) !== key));
      } else if (!cur.some((it) => _grid(it.lat, it.lng) === key)) {
        _writeLocal([...cur, { lat, lng, label: label || 'Area',
                                 added_at: new Date().toISOString() }]);
      }
    } catch { ok = false; }
    finally {
      if (!ok) {
        // Revert optimistic change.
        if (wasOn) _hearted.add(key); else _hearted.delete(key);
        _notify();
      }
      setBusy(false);
    }
  }, [lat, lng, label, key]);

  const dim = size === 'lg' ? 'h-5 w-5' : size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={on}
      aria-label={on ? 'Remove from wishlist' : 'Save to wishlist'}
      title={on ? 'In your wishlist — tap to remove' : 'Save to wishlist'}
      data-testid={`wishlist-heart-${key}`}
      className={`inline-flex items-center justify-center rounded-full p-1 transition-colors ${
        on ? 'text-rose-500 hover:text-rose-600' : 'text-gray-400 hover:text-rose-500'
      } ${className}`}
    >
      <Heart className={`${dim} ${on ? 'fill-current' : ''}`} />
    </button>
  );
};

// Utility for AuthContext callers — merges any anonymous local hearts
// into the account when the buyer signs in.
export const syncWishlistLocalToServer = async () => {
  const token = window.localStorage.getItem('token');
  if (!token) return;
  const local = _readLocal();
  if (!local.length) return;
  for (const it of local) {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wishlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
                     Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lat: it.lat, lng: it.lng, label: it.label }),
      });
    } catch { /* silent */ }
  }
};

export default WishlistHeart;
